import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { config } from '../config';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../utils/toastUtils';
import { useTranslation } from 'react-i18next';

// Define a type for a batched click with retry metadata
type BatchedClick = {
  buttonId: string;
  counterId: string;
  timestamp: number;
  retryCount: number;
  cost: number; // Track the cost of each click
};

// Define a type for the batch sending result
type BatchSendResult = {
  success: boolean;
  failedButtonIds: string[];
  insufficientBalanceButtonIds?: string[]; // Track buttons that failed due to insufficient balance
};

// Custom event name for insufficient tokens
const INSUFFICIENT_TOKENS_EVENT = 'insufficientTokens';

// Get batch configuration from centralized config
const BATCH_INTERVAL = config.batching.interval; // Default batch interval from config
const MAX_BATCH_SIZE = config.batching.size;     // Default batch size from config
const MAX_RETRIES = 3;                           // Maximum number of retries for failed batches

/**
 * Hook that provides click batching functionality
 * 
 * This hook implements an efficient click batching system that:
 * 1. Collects clicks in memory until they're ready to be sent
 * 2. Groups clicks by buttonId to reduce payload size
 * 3. Tracks pending clicks for UI feedback
 * 4. Handles failures with automatic retries
 * 5. Prevents duplicate sends of the same clicks
 * 6. Tracks token costs and prevents clicking when balance is insufficient
 * 
 * @param counterId - The ID of the counter
 * @param isOwnerUrl - Whether the counter ID is an owner URL
 * @returns Object with click registration functions and status information
 */
export function useClickBatcher(counterId: string, isOwnerUrl = false) {
  // Get user info for token transactions
  const { user } = useUser();
  const userEmail = user.email;
  const { showToast } = useToast();
  const { t } = useTranslation();
  
  // Store pending clicks that haven't been sent to the server yet
  const [pendingClicks, setPendingClicks] = useState<Record<string, number>>({});
  
  // Reference to the click queue to avoid closure issues in timers
  const clickQueueRef = useRef<BatchedClick[]>([]);
  
  // Track which buttons have failed to update
  const [failedButtons, setFailedButtons] = useState<string[]>([]);
  
  // Track buttons with insufficient balance
  const [insufficientBalanceButtons, setInsufficientBalanceButtons] = useState<string[]>([]);
  
  // Flag to track if a batch is currently being sent
  const isSendingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Track which clicks are currently being processed
  const inProgressBatchRef = useRef<BatchedClick[]>([]);
  
  // Timer reference
  const batchTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Optional UI update callback
  const [uiUpdateCallback, setUiUpdateCallback] = useState<((buttonId: string) => void) | null>(null);
  
  // Reference to button costs cache
  const buttonCostsRef = useRef<Record<string, number>>({});
  
  /**
   * Trigger a custom event to notify of insufficient tokens
   */
  const triggerInsufficientTokensEvent = (buttonId: string, cost: number) => {
    const event = new CustomEvent(INSUFFICIENT_TOKENS_EVENT, { 
      detail: { buttonId, cost } 
    });
    window.dispatchEvent(event);
  };
  
  /**
   * Set a callback to be triggered when a click is registered
   * This is used for optimistic UI updates that don't rely on WebSocket
   */
  const setUpdateCallback = useCallback((callback: (buttonId: string) => void) => {
    setUiUpdateCallback(() => callback);
  }, []);
  
  /**
   * Queue a click for batched sending
   * 
   * This adds the click to the queue and updates the UI state,
   * but doesn't immediately send to the server.
   */
  const queueClick = useCallback((buttonId: string, buttonCost?: number) => {
    // Store button cost for future reference if provided
    if (buttonCost !== undefined) {
      buttonCostsRef.current[buttonId] = buttonCost;
    }
    
    // Get the stored cost or default to 0
    const cost = buttonCostsRef.current[buttonId] || 0;
    
    // Add to the queue for batched sending
    clickQueueRef.current.push({
      buttonId,
      counterId,
      timestamp: Date.now(),
      retryCount: 0,
      cost
    });
    
    // Update pendingClicks for UI feedback
    setPendingClicks(prev => ({
      ...prev,
      [buttonId]: (prev[buttonId] || 0) + 1
    }));
    
    // Call the UI update callback if available
    if (uiUpdateCallback) {
      uiUpdateCallback(buttonId);
    }
    
    // If a batch timer isn't running, start one
    if (!batchTimerRef.current) {
      startBatchTimer();
    }
  }, [counterId, uiUpdateCallback]);
  
  /**
   * Start the timer for sending batches
   * 
   * This ensures clicks are sent in batches after a delay, 
   * to optimize network usage.
   */
  const startBatchTimer = useCallback(() => {
    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
    }
    
    batchTimerRef.current = setTimeout(() => {
      sendBatch();
      batchTimerRef.current = null;
    }, BATCH_INTERVAL);
  }, []);
  
  /**
   * Send a batch of clicks to the server
   * 
   * This function:
   * 1. Takes a chunk of clicks from the queue
   * 2. Groups them by buttonId for efficiency
   * 3. Sends them to the server
   * 4. Handles success/failure responses
   * 5. Updates UI state accordingly
   */
  const sendBatch = useCallback(async () => {
    // If we're already sending or there's nothing to send, do nothing
    if (isSendingRef.current || clickQueueRef.current.length === 0) {
      return;
    }
    
    // Mark as sending
    isSendingRef.current = true;
    setIsProcessing(true);
    
    try {
      // Take a chunk of clicks to send, but don't include any that are currently in progress
      const clicksToIgnore = new Set(inProgressBatchRef.current.map(c => `${c.buttonId}-${c.timestamp}`));
      
      // Filter out clicks that are already being processed
      const availableClicks = clickQueueRef.current.filter(click => {
        const clickId = `${click.buttonId}-${click.timestamp}`;
        return !clicksToIgnore.has(clickId) && click.retryCount < MAX_RETRIES;
      });
      
      // Take the first batch of available clicks
      const batchToSend = availableClicks.slice(0, MAX_BATCH_SIZE);
      
      // Store the batch we're processing so we don't duplicate
      inProgressBatchRef.current = [...inProgressBatchRef.current, ...batchToSend];
      
      // If there are no valid clicks to send, clear the sending flag and return
      if (batchToSend.length === 0) {
        isSendingRef.current = false;
        setIsProcessing(false);
        return;
      }
      
      // Group clicks by buttonId for efficient batching
      const groupedClicks: Record<string, number> = {};
      batchToSend.forEach(click => {
        groupedClicks[click.buttonId] = (groupedClicks[click.buttonId] || 0) + 1;
      });
      
      // Send the batch
      const result = await sendClickBatch(counterId, groupedClicks, isOwnerUrl);
      
      // Handle the result
      if (result.success) {
        // Create a set of sent clicks for faster lookup
        const sentClickIds = new Set(batchToSend.map(c => `${c.buttonId}-${c.timestamp}`));
        
        // Remove sent clicks from the main queue
        clickQueueRef.current = clickQueueRef.current.filter(click => {
          const clickId = `${click.buttonId}-${click.timestamp}`;
          return !sentClickIds.has(clickId);
        });
        
        // Also remove them from the in-progress batch
        inProgressBatchRef.current = inProgressBatchRef.current.filter(click => {
          const clickId = `${click.buttonId}-${click.timestamp}`;
          return !sentClickIds.has(clickId);
        });
        
        // Update pendingClicks state by removing successful clicks
        setPendingClicks(prev => {
          const newPending = { ...prev };
          Object.keys(groupedClicks).forEach(buttonId => {
            // If this button had insufficient balance issues, don't remove all pending clicks
            if (result.insufficientBalanceButtonIds?.includes(buttonId)) {
              // Leave some pending to indicate the failure
              const failedCount = groupedClicks[buttonId];
              newPending[buttonId] = failedCount;
            } else {
              newPending[buttonId] = Math.max(0, (newPending[buttonId] || 0) - groupedClicks[buttonId]);
              if (newPending[buttonId] === 0) {
                delete newPending[buttonId];
              }
            }
          });
          return newPending;
        });
        
        // Handle any insufficient balance buttons
        if (result.insufficientBalanceButtonIds && result.insufficientBalanceButtonIds.length > 0) {
          // Update the insufficient balance buttons list
          setInsufficientBalanceButtons(result.insufficientBalanceButtonIds);
          
          // Trigger the insufficient tokens event for the UI to respond
          if (result.insufficientBalanceButtonIds.length > 0) {
            const buttonId = result.insufficientBalanceButtonIds[0];
            const cost = buttonCostsRef.current[buttonId] || 0;
            triggerInsufficientTokensEvent(buttonId, cost);
          }
        } else {
          // Clear the insufficient balance list if no issues
          setInsufficientBalanceButtons([]);
        }
        
        // Remove buttons from the failed list if they succeeded
        if (failedButtons.length > 0) {
          const succeededButtonIds = Object.keys(groupedClicks).filter(
            id => !result.insufficientBalanceButtonIds?.includes(id)
          );
          setFailedButtons(prev => prev.filter(id => !succeededButtonIds.includes(id)));
        }
      } else {
        // Mark failed clicks for retry
        const failedClickMap = new Set(result.failedButtonIds);
        
        // Update retry counts for failed clicks
        clickQueueRef.current.forEach(click => {
          if (failedClickMap.has(click.buttonId)) {
            click.retryCount++;
          }
        });
        
        // Remove from in-progress batch but keep in main queue for retry
        inProgressBatchRef.current = inProgressBatchRef.current.filter(click => 
          !failedClickMap.has(click.buttonId)
        );
        
        // Track failed buttons for UI feedback
        setFailedButtons(result.failedButtonIds);
      }
    } catch (error) {
      // Enhanced error handling for token issues and network errors
      console.error('Error in batch sending:', error);
      
      // Clear the in-progress batch on error
      inProgressBatchRef.current = [];
      
      // Check for specific token-related errors
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
          // The ButtonList component will handle insufficient tokens with a modal
          // No need to show a toast here as the modal will be displayed
          
          // Update the UI to show insufficient funds warning
          // Extract the button ID if available in the error message
          const match = errorMsg.match(/button[_:]\s*([a-f0-9]+)/i);
          const buttonId = match ? match[1] : null;
          
          if (buttonId) {
            setInsufficientBalanceButtons([buttonId]);
            const cost = buttonCostsRef.current[buttonId] || 0;
            triggerInsufficientTokensEvent(buttonId, cost);
          }
        } else if (errorMsg.includes('not found') || errorMsg.includes('account')) {
          showToast(t('errors.accountNotFound', 'Your account is being created. Please try again in a moment.'), 'info');
        } else if (errorMsg.includes('token') || errorMsg.includes('transfer')) {
          showToast(t('errors.tokenTransferFailed', 'Token transfer failed. Please try again.'), 'warning');
        } else {
          // Generic network error message
          showToast(t('errors.clickNetworkError', 'Network error: Could not process click'), 'warning');
        }
      } else {
        // Generic error message
        showToast(t('errors.clickNetworkError', 'Network error: Could not process click'), 'warning');
      }
    } finally {
      // Clear the sending flag
      isSendingRef.current = false;
      setIsProcessing(false);
      
      // If there are still clicks to send, start another timer
      if (clickQueueRef.current.length > 0) {
        // Check if the batch has been retried too many times
        let maxRetriesExceeded = false;
        
        // Check if any clicks have exceeded MAX_RETRIES
        clickQueueRef.current.forEach(click => {
          if (click.retryCount >= MAX_RETRIES) {
            maxRetriesExceeded = true;
          }
        });
        
        if (maxRetriesExceeded) {
          // Remove clicks that have been retried too many times
          clickQueueRef.current = clickQueueRef.current.filter(click => click.retryCount < MAX_RETRIES);
          
          // Update pendingClicks state to reflect dropped clicks
          setPendingClicks({});
          
          if (clickQueueRef.current.length > 0) {
            startBatchTimer();
          }
        } else {
          startBatchTimer();
        }
      }
    }
  }, [counterId, isOwnerUrl, failedButtons, t, showToast]);
  
  /**
   * Send a batch of clicks to the server using the API
   * 
   * @param counterId - The counter ID
   * @param clicks - Object mapping buttonIds to click counts
   * @param isOwnerUrl - Whether using owner URL for authentication
   * @returns Result object indicating success or failure
   */
  const sendClickBatch = async (
    counterId: string, 
    clicks: Record<string, number>, 
    isOwnerUrl: boolean
  ): Promise<BatchSendResult> => {
    try {
      const url = `${config.api.baseUrl}/api/counters/${counterId}/clicks/batch${isOwnerUrl ? '?isOwnerUrl=true' : ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          clicks,
          userEmail // Pass user email for P2P token transfers 
        })
      });
      
      // Parse the response JSON with error handling
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        // If we can't parse the response, consider it a failure
        return {
          success: false,
          failedButtonIds: Object.keys(clicks)
        };
      }
      
      // Handle partial success (HTTP 207)
      if (response.status === 207) {
        // Check for token errors and show appropriate messages
        if (result.tokenErrors && Object.keys(result.tokenErrors).length > 0) {
          // Extract buttons with insufficient balance
          const insufficientBalanceButtons = Object.entries(result.tokenErrors)
            .filter(([_, code]) => code === 'INSUFFICIENT_BALANCE')
            .map(([buttonId]) => buttonId);
            
          if (insufficientBalanceButtons.length > 0) {
            // Return partial success with info about insufficient balance buttons
            return {
              success: true,
              failedButtonIds: result.failedButtonIds || [],
              insufficientBalanceButtonIds: insufficientBalanceButtons
            };
          } else if (result.tokenErrors[Object.keys(result.tokenErrors)[0]] === 'SENDER_NOT_FOUND') {
            showToast(t('errors.accountNotFound', 'Your account is being created. Please try again in a moment.'), 'info');
          } else {
            showToast(t('errors.tokenTransferFailed', 'Token transfer failed. Please try again.'), 'warning');
          }
        }
        
        return {
          success: true, // We still count partial success as success 
          failedButtonIds: result.failedButtonIds || []
        };
      }
      
      // Handle failure
      if (!response.ok) {
        // Extract failed button IDs from the error response if available
        const failedIds = result?.failedButtonIds || Object.keys(clicks);
        return {
          success: false,
          failedButtonIds: failedIds
        };
      }
      
      // Handle success
      return {
        success: true,
        failedButtonIds: []
      };
    } catch (error) {
      console.error('Error sending click batch:', error);
      return {
        success: false,
        failedButtonIds: Object.keys(clicks)
      };
    }
  };
  
  // Set up cleanup on unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }
    };
  }, []);
  
  return {
    /**
     * Register a click on a button for batched sending
     * @param buttonId The button ID that was clicked
     * @param cost Optional cost of the button click (for token tracking)
     */
    registerClick: queueClick,
    
    /**
     * Set a callback to handle UI updates when clicks are registered
     * @param callback The callback function to be called with the buttonId
     */
    setUpdateCallback,
    
    /**
     * Whether a batch is currently being sent
     */
    isProcessing,
    
    /**
     * Check if a button has pending clicks
     * @param buttonId The button ID to check
     * @returns Boolean indicating if there are pending clicks
     */
    hasPendingClicks: (buttonId: string) => !!pendingClicks[buttonId],
    
    /**
     * Get the number of pending clicks for a button
     * @param buttonId The button ID to check
     * @returns Number of pending clicks
     */
    getPendingClickCount: (buttonId: string) => pendingClicks[buttonId] || 0,
    
    /**
     * Check if a button has failed updates
     * @param buttonId The button ID to check
     * @returns Boolean indicating if there are failed updates
     */
    hasFailedUpdates: (buttonId: string) => 
      failedButtons.includes(buttonId) || insufficientBalanceButtons.includes(buttonId),
    
    /**
     * Get all buttons with pending clicks
     * @returns Array of button IDs with pending clicks
     */
    getPendingButtonIds: () => Object.keys(pendingClicks),
    
    /**
     * Force send the current batch immediately
     */
    forceSendBatch: () => {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
      sendBatch();
    },
    
    /**
     * Get the name of the insufficient tokens event
     * For components to listen to insufficient tokens events
     */
    INSUFFICIENT_TOKENS_EVENT
  };
} 